import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "shadcn-card rounded-xl border bg-card border-card-border text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
));
Card.displayName = "Card"

interface InteractiveCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Called when the card is clicked or activated via keyboard */
  onActivate?: () => void
  /** Accessible label for the card */
  "aria-label"?: string
}

/**
 * An interactive card component with built-in keyboard navigation.
 * Use this when a card is clickable and should be keyboard accessible.
 *
 * Features:
 * - Focusable via Tab key
 * - Activatable via Enter or Space
 * - Visible focus indicator
 * - Proper ARIA role
 */
const InteractiveCard = React.forwardRef<HTMLDivElement, InteractiveCardProps>(
  ({ className, onActivate, onClick, onKeyDown, ...props }, ref) => {
    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onActivate?.()
        }
        onKeyDown?.(event)
      },
      [onActivate, onKeyDown]
    )

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        onActivate?.()
        onClick?.(event)
      },
      [onActivate, onClick]
    )

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        className={cn(
          "shadcn-card rounded-xl border bg-card border-card-border text-card-foreground shadow-sm",
          "cursor-pointer transition-all",
          "hover:border-primary hover:shadow-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          className
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props}
      />
    )
  }
)
InteractiveCard.displayName = "InteractiveCard"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"
export {
  Card,
  InteractiveCard,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
}
